/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { useCosmosComponentContext } from "@cosmos/components";
import debounce from "lodash.debounce";
import { useState, useEffect, useMemo } from "react";

import { CosmosSearchResponse } from "../types";

import { getSearchApiUrl } from "./search-url.utils";

interface UseSearchReturn {
	response?: CosmosSearchResponse;
	error: boolean;
	isLoading: boolean;
}

export const useSearch = (
	query = "",
	locale = "en",
	filter?: string,
	slug = "/search",
	debounceDelay = 300,
): UseSearchReturn => {
	const { serverUrl, siteBasePath } = useCosmosComponentContext();
	const [response, setResponse] = useState<CosmosSearchResponse | undefined>();
	const [error, setError] = useState(false);

	const debounceFetch = useMemo(
		() =>
			debounce((query: string, locale: string, filter: string, slug: string, controller: AbortController) => {
				setError(false);
				setResponse(undefined);
				if (!query?.length) {
					setResponse({ items: [], total: 0 });
					return;
				}

				const params = new URLSearchParams({
					query,
					slug,
					locale,
					filter,
				});

				const url = getSearchApiUrl(params, { serverUrl, siteBasePath, fallbackOrigin: location.origin });

				fetch(url, { signal: controller.signal })
					.then((res) => {
						if (!res.ok) throw new Error("Network response was not ok");
						return res.json();
					})
					.then((response) => {
						setResponse(response);
					})
					.catch((err) => {
						if (err.name !== "AbortError") setError(true);
					});
			}, debounceDelay || 300),
		[setError, setResponse, debounceDelay, serverUrl, siteBasePath],
	);

	useEffect(() => {
		const controller = new AbortController();
		debounceFetch(query, locale, filter || "", slug, controller);

		return () => {
			controller.abort();
		};
	}, [query, locale, filter, slug, debounceFetch]);

	return { response, error, isLoading: !response && !error };
};
